import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faClock, faLock, faMobileScreenButton, faPlay, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';
import { INTERNAL_PROGRAM } from '../../config/deployProgram';
import { BetaTestReport, DeployTest, PhoneDevice, User } from '../../types';
import { resolveTester } from '../../utils/tester';
import { firmwareTypeLabel } from './deployTestUtils';

interface TestingHomeViewProps {
  user: User;
}

const formatDate = (value?: string | Date) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const TestingHomeView: React.FC<TestingHomeViewProps> = ({ user }) => {
  const navigate = useNavigate();
  const tester = resolveTester(user);
  const isTester = tester === 'internal';

  const [loading, setLoading] = useState(isTester);
  const [currentTest, setCurrentTest] = useState<DeployTest | null>(null);
  const [currentReport, setCurrentReport] = useState<BetaTestReport | null>(null);
  const [testInstances, setTestInstances] = useState<Array<{ test: DeployTest; report: BetaTestReport | null; phoneDevice: PhoneDevice }>>([]);
  const [starting, setStarting] = useState(false);
  const [rejectingKey, setRejectingKey] = useState('');
  const [loadError, setLoadError] = useState('');
  const [phoneDevices, setPhoneDevices] = useState<PhoneDevice[]>(user.phoneDevices || []);
  const [phoneDeviceName, setPhoneDeviceName] = useState('');
  const [phoneDevicePlatform, setPhoneDevicePlatform] = useState<PhoneDevice['platform']>('ios');
  const [addingPhoneDevice, setAddingPhoneDevice] = useState(false);
  const [savingPhoneDevice, setSavingPhoneDevice] = useState(false);

  const loadCurrent = useCallback(async () => {
    if (!isTester) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}${INTERNAL_PROGRAM.reportsApi}/current`, {
        withCredentials: true,
      });
      setCurrentTest(response.data.test || null);
      setCurrentReport(response.data.report || null);
      setTestInstances(response.data.instances || []);
      setLoadError('');
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not load the current test.';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [isTester]);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  const handleStartTest = async (test: DeployTest, phoneDevice?: PhoneDevice) => {
    if (!test || starting) return;
    setStarting(true);
    setLoadError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}${INTERNAL_PROGRAM.reportsApi}`,
        { deployTestId: test._id, phoneDeviceId: phoneDevice?._id || test.phoneDevice?._id },
        { withCredentials: true },
      );
      const testId = response.data.test?._id || test._id;
      const deviceId = response.data.test?.phoneDevice?._id || phoneDevice?._id || test.phoneDevice?._id;
      navigate(`/take/${testId}${deviceId ? `?phoneDeviceId=${deviceId}` : ''}`);
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not start the test.';
      setLoadError(message);
      setStarting(false);
    }
  };

  const addPhoneDevice = async () => {
    if (!phoneDeviceName.trim() || savingPhoneDevice) return;
    setSavingPhoneDevice(true);
    setLoadError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/me/phone-devices`,
        { name: phoneDeviceName.trim(), platform: phoneDevicePlatform },
        { withCredentials: true },
      );
      setPhoneDevices(response.data.user?.phoneDevices || []);
      setPhoneDeviceName('');
      setPhoneDevicePlatform('ios');
      setAddingPhoneDevice(false);
      await loadCurrent();
    } catch (err) {
      setLoadError(
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not add phone device.'
      );
    } finally {
      setSavingPhoneDevice(false);
    }
  };

  const removePhoneDevice = async (deviceId?: string) => {
    if (!deviceId || savingPhoneDevice) return;
    setSavingPhoneDevice(true);
    setLoadError('');
    try {
      const response = await axios.delete(`${API_BASE_URL}/auth/me/phone-devices/${deviceId}`, {
        withCredentials: true,
      });
      setPhoneDevices(response.data.user?.phoneDevices || []);
      await loadCurrent();
    } catch (err) {
      setLoadError(
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not remove phone device.'
      );
    } finally {
      setSavingPhoneDevice(false);
    }
  };

  const rejectTest = async (test: DeployTest, phoneDevice?: PhoneDevice) => {
    const deviceId = phoneDevice?._id || test.phoneDevice?._id;
    if (!test || !deviceId || rejectingKey) return;
    setRejectingKey(`${test._id}-${deviceId}`);
    setLoadError('');
    try {
      await axios.post(
        `${API_BASE_URL}${INTERNAL_PROGRAM.reportsApi}/reject`,
        { deployTestId: test._id, phoneDeviceId: deviceId },
        { withCredentials: true },
      );
      await loadCurrent();
    } catch (err) {
      setLoadError(
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not reject the test.'
      );
    } finally {
      setRejectingKey('');
    }
  };

  if (!isTester) {
    return (
      <div className="testing-home">
        <div className="testing-home-status">
          <div className="testing-home-status-icon is-locked">
            <FontAwesomeIcon icon={faLock} />
          </div>
          <div className="testing-home-status-kicker">Internal testing</div>
          <h1>Access required</h1>
          <p>
            You are not currently an internal tester, please reach out to an
            administrator.
          </p>
        </div>
      </div>
    );
  }

  const activeInstances = testInstances.filter((instance) => (
    instance.report?.status !== 'completed' && instance.report?.status !== 'rejected'
  ));
  const showStartCard = activeInstances.length > 0 || (
    currentTest && currentReport?.status !== 'completed' && currentReport?.status !== 'rejected'
  );
  const completedTest = currentTest && (currentReport?.status === 'completed' || currentReport?.status === 'rejected');

  return (
    <div className="testing-home">
      <div className="testing-home-welcome">
        <div className="testing-home-welcome-copy">
          <div className="testing-home-kicker">Internal Testing</div>
          <h1>Welcome{user.firstName ? `, ${user.firstName}` : ''}</h1>
        </div>
        <span className="testing-home-badge">Internal tester</span>
      </div>

      <div className="testing-home-test-card mb-3">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
          <div className="testing-home-test-kicker mb-0">
            <FontAwesomeIcon icon={faMobileScreenButton} className="me-2" />
            Phone devices
          </div>
          {!addingPhoneDevice && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setAddingPhoneDevice(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Add
            </button>
          )}
        </div>
        {phoneDevices.length === 0 ? (
          <p className="text-muted">Add the phones you will use for internal testing.</p>
        ) : (
          <div className="d-flex flex-column gap-2 mb-3">
            {phoneDevices.map((device) => (
              <div
                key={device._id || `${device.platform}-${device.name}`}
                className="d-flex align-items-center justify-content-between gap-3 border rounded p-2"
              >
                <div className="min-w-0">
                  <strong>{device.name}</strong>
                  <span className="badge text-bg-light text-uppercase ms-2">{device.platform}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger flex-shrink-0"
                  onClick={() => removePhoneDevice(device._id)}
                  disabled={savingPhoneDevice}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        {addingPhoneDevice && (
          <div className="border rounded-3 bg-light p-3 mt-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-6">
                <label htmlFor="testing-phone-name" className="form-label fw-bold">Device Name</label>
                <input
                  id="testing-phone-name"
                  className="form-control"
                  value={phoneDeviceName}
                  onChange={(event) => setPhoneDeviceName(event.target.value)}
                  placeholder="iPhone 16 Pro"
                  disabled={savingPhoneDevice}
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="testing-phone-platform" className="form-label fw-bold">Platform</label>
                <select
                  id="testing-phone-platform"
                  className="form-select"
                  value={phoneDevicePlatform}
                  onChange={(event) => setPhoneDevicePlatform(event.target.value as PhoneDevice['platform'])}
                  disabled={savingPhoneDevice}
                >
                  <option value="ios">iOS</option>
                  <option value="android">Android</option>
                </select>
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary flex-grow-1"
                  onClick={addPhoneDevice}
                  disabled={savingPhoneDevice || !phoneDeviceName.trim()}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setAddingPhoneDevice(false);
                    setPhoneDeviceName('');
                    setPhoneDevicePlatform('ios');
                  }}
                  disabled={savingPhoneDevice}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="testing-home-status">
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      ) : showStartCard && (activeInstances.length || currentTest) ? (
        <div className="d-flex flex-column gap-3">
          {(activeInstances.length ? activeInstances : [{ test: currentTest!, report: currentReport, phoneDevice: currentTest!.phoneDevice as PhoneDevice }]).map((instance) => {
            const instanceTest = instance.test;
            const instanceReport = instance.report;
            const instanceContinue =
              instanceReport?.status === 'in_progress' && !instanceReport?.awaitingStart;
            const startedOnPhone = testInstances.find((other) => (
              String(other.phoneDevice?._id || '') === String(instance.phoneDevice?._id || '')
              && other.report?.status === 'in_progress'
              && !other.report?.awaitingStart
            ));
            const blockedByOtherTest = Boolean(
              startedOnPhone && String(startedOnPhone.test._id) !== String(instanceTest._id)
            );
            const firmwareLabel = firmwareTypeLabel(instanceTest.firmwareType);
            return (
              <div className="testing-home-test-card" key={`${instanceTest._id}-${instance.phoneDevice?._id || 'default'}`}>
                <div className="testing-home-test-kicker">
                  Internal Test
                  {instanceReport?.round ? ` · Round ${instanceReport.round}` : ''}
                </div>
                <h2>{instanceTest.name}</h2>
                {instanceTest.createdAt && (
                  <div className="testing-home-test-date">{formatDate(instanceTest.createdAt)}</div>
                )}
                <div className="testing-home-versions">
                  <div>
                    <span>Phone</span>
                    <strong>{instance.phoneDevice?.name || 'Phone'}</strong>
                  </div>
                  <div>
                    <span>Platform</span>
                    <strong>{instance.phoneDevice?.platform?.toUpperCase() || 'APP'}</strong>
                  </div>
                  {instanceTest.firmwareVersion && (
                    <div>
                      <span>Firmware</span>
                      <strong>
                        v{instanceTest.firmwareVersion}
                        {firmwareLabel ? ` (${firmwareLabel})` : ''}
                      </strong>
                    </div>
                  )}
                  {instance.phoneDevice?.platform === 'ios' && (instanceTest.mobileAppVersionIos || instanceTest.mobileAppVersion) && (
                    <div>
                      <span>Mobile App (iOS)</span>
                      <strong>v{instanceTest.mobileAppVersionIos || instanceTest.mobileAppVersion}</strong>
                    </div>
                  )}
                  {instance.phoneDevice?.platform === 'android' && (instanceTest.mobileAppVersionAndroid || instanceTest.mobileAppVersion) && (
                    <div>
                      <span>Mobile App (Android)</span>
                      <strong>v{instanceTest.mobileAppVersionAndroid || instanceTest.mobileAppVersion}</strong>
                    </div>
                  )}
                </div>
                {instanceTest.overview?.trim() && (
                  <div className="testing-home-overview">
                    <span>Instructions</span>
                    <p>{instanceTest.overview}</p>
                  </div>
                )}
                {blockedByOtherTest && startedOnPhone ? (
                  <div className="alert alert-warning mb-3" role="alert">
                    Finish or exit {startedOnPhone.test.name} on this phone before starting another test.
                  </div>
                ) : null}
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg testing-home-start-btn flex-grow-1"
                    onClick={() => handleStartTest(instanceTest, instance.phoneDevice)}
                    disabled={starting || Boolean(rejectingKey) || blockedByOtherTest}
                  >
                    <FontAwesomeIcon icon={faPlay} className="me-2" />
                    {starting
                      ? 'Starting…'
                      : instanceReport?.round && instanceReport.round > 1
                        ? `${instanceContinue ? 'Continue' : 'Start'} Round ${instanceReport.round}`
                        : instanceContinue
                          ? 'Continue Test'
                          : 'Start Test'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-lg"
                    onClick={() => rejectTest(instanceTest, instance.phoneDevice)}
                    disabled={starting || rejectingKey === `${instanceTest._id}-${instance.phoneDevice?._id || ''}`}
                  >
                    {rejectingKey === `${instanceTest._id}-${instance.phoneDevice?._id || ''}` ? 'Rejecting…' : 'Reject'}
                  </button>
                </div>
              </div>
            );
          })}
          {loadError && (
            <div className="alert alert-danger" role="alert">
              {loadError}
            </div>
          )}
        </div>
      ) : (
        <div className="testing-home-status">
          {loadError ? (
            <div className="alert alert-danger mb-0" role="alert">
              {loadError}
            </div>
          ) : (
            <>
              <div className={`testing-home-status-icon${completedTest ? ' is-done' : ''}`}>
                <FontAwesomeIcon icon={completedTest ? faCircleCheck : faClock} />
              </div>
              <div className="testing-home-status-kicker">
                {completedTest ? 'Completed' : 'Waiting'}
              </div>
              <h2>{completedTest ? "You're all caught up" : 'No active test'}</h2>
              <p>
                {completedTest
                  ? `You've completed ${currentTest.name}. We'll let you know when the next test is ready.`
                  : "There isn't an internal test in progress right now. Check back soon."}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TestingHomeView;
